// Wizard flow + reference data for the Indilabs internship application

export const TRACKS = [
  {
    value: 'ai-ml',
    label: 'AI / Machine Learning',
    icon: 'BrainCircuit',
    desc: 'NLP, computer vision, LLM fine-tuning, RAG',
    skills: ['Python', 'PyTorch', 'TensorFlow', 'NLP', 'Computer Vision', 'LLM / RAG', 'Hugging Face', 'OpenCV', 'Scikit-learn', 'Data Wrangling'],
  },
  {
    value: 'edge-robotics',
    label: 'Edge & Robotics',
    icon: 'Cpu',
    desc: 'Embedded AI, drone, robot pengawas',
    skills: ['C / C++', 'Arduino', 'Raspberry Pi', 'ROS', 'Embedded Linux', 'Sensor Fusion', 'Drone / UAV', 'PCB Design', 'Microcontroller', 'Edge AI'],
  },
  {
    value: 'product-design',
    label: 'Product & Design',
    icon: 'PenTool',
    desc: 'UX research, product design, prototyping',
    skills: ['Figma', 'UX Research', 'Wireframing', 'Prototyping', 'UI Design', 'Design System', 'User Testing', 'Adobe XD', 'Illustrator', 'Product Thinking'],
  },
  {
    value: 'backend-data',
    label: 'Backend & Data',
    icon: 'Database',
    desc: 'API, pipeline data, dashboard analitik',
    skills: ['Python', 'Node.js', 'SQL', 'REST API', 'FastAPI', 'Docker', 'PostgreSQL', 'ETL / Pipeline', 'Data Viz', 'Cloud (GCP/AWS)'],
  },
  {
    value: 'marketing',
    label: 'Marketing & Growth',
    icon: 'Megaphone',
    desc: 'Sales, social media, konten, riset pasar',
    skills: ['Social Media', 'Content Writing', 'Copywriting', 'Market Research', 'SEO', 'Google / Meta Ads', 'Canva', 'Sales', 'Branding', 'Analytics'],
  },
];

export const EDUCATION_LEVELS = ['SMK', 'SMA', 'D3', 'D4', 'S1', 'S2', 'Lainnya'];

export const DURATIONS = [
  { value: '3 bulan', label: '3 bulan', hint: 'Sprint singkat' },
  { value: '4 bulan', label: '4 bulan', hint: 'Pas buat semester' },
  { value: '6 bulan', label: '6 bulan', hint: 'Proyek mendalam' },
  { value: 'Fleksibel', label: 'Fleksibel', hint: 'Bisa dibicarakan' },
];

export const SALARY = { min: 0, max: 4000000, step: 250000, default: 1500000 };

export const skillsForTrack = (track) =>
  TRACKS.find((t) => t.value === track)?.skills || [];

// Small open-ended skill test (5 questions per track). No right/wrong answers —
// reviewed manually by HRD. Used by the 'skill-test' wizard step.
export const SKILL_TEST_DEFAULT = [
  'Ceritakan satu proyek atau pengalaman yang paling kamu banggakan di bidang ini.',
  'Skill apa yang paling kamu kuasai, dan bagaimana kamu mempelajarinya?',
  'Bagaimana caramu menyelesaikan masalah saat menemui kesulitan teknis?',
  'Hal baru apa yang sedang atau ingin kamu pelajari belakangan ini?',
  'Kenapa kamu cocok untuk bidang yang kamu pilih ini?',
];

export const SKILL_TEST = {
  'ai-ml': [
    'Ceritakan satu proyek/eksperimen ML yang pernah kamu buat. Apa masalahnya dan pendekatan yang kamu pakai?',
    'Dengan bahasamu sendiri, apa beda overfitting dan underfitting, dan bagaimana cara menanganinya?',
    'Kalau diberi dataset yang kotor (banyak data hilang/duplikat), langkah pertama apa yang kamu lakukan?',
    'Sebutkan satu konsep atau model AI yang baru kamu pelajari belakangan ini dan kenapa menarik buatmu.',
    'Bagaimana kamu menilai apakah sebuah model sudah "cukup baik" untuk dipakai?',
  ],
  'edge-robotics': [
    'Ceritakan proyek hardware, robotik, atau embedded yang pernah kamu kerjakan.',
    'Bagaimana caramu men-debug perangkat yang tidak menyala atau sensor yang bacaannya aneh?',
    'Apa pertimbangan utamamu saat memilih mikrokontroler atau komponen untuk sebuah proyek?',
    'Jelaskan satu konsep (misalnya PWM, I2C, atau sensor fusion) dengan bahasamu sendiri.',
    'Bagaimana kamu memastikan sistem tetap aman dan stabil saat dijalankan di lapangan?',
  ],
  'product-design': [
    'Ceritakan prosesmu dari ide sampai prototipe untuk satu produk atau fitur.',
    'Bagaimana caramu memahami kebutuhan pengguna sebelum mulai mendesain?',
    'Saat ada dua pilihan desain, bagaimana kamu memutuskan mana yang lebih baik?',
    'Tools desain apa yang kamu kuasai, dan biasanya kamu pakai untuk apa?',
    'Ceritakan satu masukan yang pernah mengubah desainmu, dan bagaimana kamu menanggapinya.',
  ],
  'backend-data': [
    'Ceritakan satu API atau pipeline data yang pernah kamu bangun.',
    'Bagaimana caramu mendesain struktur database untuk sebuah fitur baru?',
    'Apa yang kamu lakukan saat sebuah endpoint atau aplikasi terasa lambat?',
    'Jelaskan satu konsep (misalnya REST, indexing, atau ETL) dengan bahasamu sendiri.',
    'Bagaimana caramu menjaga keamanan dan keandalan data?',
  ],
  marketing: [
    'Ceritakan satu ide atau kampanye pemasaran yang pernah kamu jalankan atau ingin kamu coba.',
    'Bagaimana caramu mengenali target audiens dari sebuah produk?',
    'Konten seperti apa yang menurutmu efektif di media sosial untuk produk teknologi?',
    'Bagaimana kamu mengukur keberhasilan sebuah kampanye atau aktivitas pemasaran?',
    'Ceritakan cara kamu melakukan riset pasar atau kompetitor secara sederhana.',
  ],
};

export const skillTestQuestions = (track) => SKILL_TEST[track] || SKILL_TEST_DEFAULT;

// One step = one screen.
export const FLOW = [
  {
    key: 'fullName', type: 'text', section: 'Kenalan',
    title: 'Siapa nama lengkapmu?',
    hint: 'Tulis sesuai dokumen resmi, ya.',
    placeholder: 'Nama lengkap',
    validate: (v) => (!v || v.trim().length < 2 ? 'Isi nama lengkapmu dulu, ya.' : null),
  },
  {
    key: 'photo', type: 'photo', section: 'Kenalan', optional: true,
    title: 'Foto profil kamu',
    hint: 'Opsional. Unggah pas foto atau foto terbaikmu. Format JPG, PNG, atau WEBP, maks 5MB.',
  },
  {
    key: 'email', type: 'email', section: 'Kontak',
    title: 'Email aktif kamu apa?',
    hint: 'Kabar seleksi akan kami kirim ke alamat ini.',
    placeholder: 'nama@email.com',
    validate: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim()) ? null : 'Format emailnya sepertinya belum pas.'),
  },
  {
    key: 'phone', type: 'tel', section: 'Kontak',
    title: 'Nomor WhatsApp yang bisa dihubungi?',
    hint: 'Pastikan nomornya aktif.',
    placeholder: '08xxxxxxxxxx',
    validate: (v) => ((v || '').replace(/\D/g, '').length < 9 ? 'Nomornya kelihatan kependekan.' : null),
  },
  {
    key: 'city', type: 'text', section: 'Kontak',
    title: 'Kamu berdomisili di kota mana?',
    hint: 'Membantu kami mengatur jadwal & lokasi.',
    placeholder: 'Contoh: Surabaya',
    validate: (v) => (!v || v.trim().length < 2 ? 'Tulis nama kotamu, ya.' : null),
  },
  {
    key: 'social', type: 'social', section: 'Kontak', optional: true,
    title: 'Akun media sosialmu',
    hint: 'Opsional, biar kami makin kenal kamu. Isi yang kamu punya saja.',
  },
  {
    key: 'education', type: 'education', section: 'Pendidikan',
    title: 'Pendidikan saat ini / pendidikan terakhirmu',
    hint: 'Jenjang, sekolah/kampus, dan jurusan.',
  },
  {
    key: 'track', type: 'single-track', section: 'Posisi',
    title: 'Kamu paling tertarik di tim mana?',
    hint: 'Pilih satu jalur magang yang paling kamu minati.',
  },
  {
    key: 'skills', type: 'multi-skill', section: 'Skill',
    title: 'Skill apa saja yang kamu kuasai?',
    hint: 'Pilih dari saran di bawah, atau tambahkan sendiri.',
  },
  {
    key: 'positionKnowledge', type: 'longtext', section: 'Skill',
    title: 'Apa yang kamu tahu soal bidang ini?',
    hint: 'Hal yang pernah kamu pelajari, baca, atau utak-atik sendiri.',
    placeholder: 'Misalnya: aku pernah bikin model deteksi objek pakai YOLO untuk tugas akhir...',
    validate: (v) => (!v || v.trim().length < 10 ? 'Cerita sedikit saja, minimal satu kalimat.' : null),
  },
  {
    key: 'skillTest', type: 'skill-test', section: 'Skill',
    title: 'Tes singkat sesuai bidangmu',
    hint: '5 pertanyaan terbuka, tidak ada jawaban benar/salah. Jawab sejujurnya sesuai pengalamanmu; jawabanmu akan direview langsung oleh tim HRD.',
  },
  {
    key: 'experience', type: 'longtext', section: 'Pengalaman',
    title: 'Punya pengalaman atau proyek yang relevan?',
    hint: 'Proyek, lomba, atau organisasi.',
    placeholder: 'Ceritakan singkat pengalamanmu...',
    validate: (v) => (!v || v.trim().length < 10 ? 'Ceritakan sedikit pengalaman atau proyekmu, ya.' : null),
  },
  {
    key: 'portfolio', type: 'links', section: 'Pengalaman',
    title: 'Tautan portofolio kamu',
    hint: 'GitHub, Behance, Dribbble, atau website pribadi. Kamu juga boleh melampirkan hasil proyek atau CV lewat link Google Drive (pastikan aksesnya disetel ke "siapa saja yang punya link"). Tambah sebanyak yang kamu mau.',
    placeholder: 'https://... (link GitHub, Google Drive, dsb.)',
    validate: (v) => (!v || v.length === 0 || !v[0] || !v[0].trim() ? 'Tambahkan minimal satu tautan portofolio, ya.' : null),
  },
  {
    key: 'motivation', type: 'longtext', section: 'Motivasi',
    title: 'Kenapa kamu mau bergabung dengan Indilabs?',
    hint: 'Ceritakan motivasimu secara singkat dan jujur.',
    placeholder: 'Apa yang bikin kamu semangat gabung di sini...',
    validate: (v) => (!v || v.trim().length < 10 ? 'Ceritakan sedikit alasanmu, ya.' : null),
  },
  {
    key: 'duration', type: 'single-duration', section: 'Preferensi',
    title: 'Berapa lama kamu berencana magang?',
    hint: 'Pilih durasi yang paling cocok dengan jadwalmu.',
  },
];

export const SECTION_ORDER = ['Kenalan', 'Kontak', 'Pendidikan', 'Posisi', 'Skill', 'Pengalaman', 'Motivasi', 'Preferensi'];
